import { useAuth0 } from "@auth0/auth0-react";
import {
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import {
  HiOutlineDotsVertical,
  HiOutlinePencil,
  HiOutlineTrash,
} from "react-icons/hi";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AuthService } from "src/common/AuthService";
import { ResetPassword } from "src/common/icons/ResetPassword";
import { Loading } from "src/common/Loading";
import { Pagination } from "src/common/Pagination";
import { formatPhoneNumber } from "src/common/phone-numbers";
import { StaffRole } from "src/common/types";
import { PageContent } from "src/layout/PageContent";
import { PageHeader, PageHeaderTitle } from "src/layout/PageHeader";
import { useDeleteStaff, useResetStaffPassword } from "src/routes/Staff/hooks";
import { useStaffQuery } from "src/routes/Staff/queries";
import { StaffFilterBar } from "src/routes/Staff/StaffFilterBar";

export const Staff = () => {
  const { user } = useAuth0();
  const query = useStaffQuery();
  const userRole = AuthService.hooks.useStaffUserRole();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDeletingStaff, deleteStaffMemberMutation, setDeletingStaffId } =
    useDeleteStaff();
  const { isResettingStaff, resetStaffPasswordMutation, setResettingStaffId } =
    useResetStaffPassword();

  if (query.isLoading || !query.data) {
    return <Loading />;
  }

  return (
    <>
      <PageHeader buttons={<StaffFilterBar />}>
        <PageHeaderTitle>GateHawk Staff</PageHeaderTitle>
      </PageHeader>
      <PageContent>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Phone Number</Th>
              <Th>Role</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {query.data.result.map((staff) => (
              <Tr key={staff.staffUserId}>
                <Td>
                  {staff.user.firstName} {staff.user.lastName}
                </Td>
                <Td>{staff.user.email}</Td>
                <Td>{formatPhoneNumber(staff.user.phone)}</Td>
                <Td textTransform="capitalize">{staff.role}</Td>
                <Td>
                  {userRole === StaffRole.Admin && (
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        aria-label="Actions"
                        icon={<HiOutlineDotsVertical />}
                        variant="clear"
                      >
                        Actions
                      </MenuButton>
                      <MenuList>
                        <MenuItem
                          justifyContent="space-between"
                          onClick={() => {
                            navigate(`/staff/${staff.staffUserId}/edit`);
                          }}
                        >
                          Edit Staff Member
                          <HiOutlinePencil />
                        </MenuItem>
                        <MenuItem
                          isDisabled={user?.sub === staff.user.idpUserId}
                          justifyContent="space-between"
                          onClick={() => {
                            setResettingStaffId(staff.staffUserId);
                          }}
                        >
                          Reset Password
                          <ResetPassword />
                        </MenuItem>
                        <MenuItem
                          isDisabled={user?.sub === staff.user.idpUserId}
                          justifyContent="space-between"
                          onClick={() => {
                            setDeletingStaffId(staff.staffUserId);
                          }}
                        >
                          Delete Staff Member
                          <HiOutlineTrash />
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        <Pagination
          unitName="user"
          total={query.data.total}
          pageNumber={parseInt(searchParams.get("pageNumber") || "1")}
          pageSize={query.data.pageSize}
          onPageChange={(page) => {
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set("pageNumber", page.toFixed(0));
              return next;
            });
          }}
        />
      </PageContent>

      <Modal
        isOpen={isDeletingStaff}
        onClose={() => {
          setDeletingStaffId(undefined);
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Staff Member</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure? You can&apos;t undo this action afterwards.
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="brand.lightGray"
              color="gray.700"
              mr={3}
              onClick={() => {
                setDeletingStaffId(undefined);
              }}
            >
              Close
            </Button>
            <Button
              colorScheme="brand.red"
              onClick={() => deleteStaffMemberMutation.mutate()}
              isDisabled={deleteStaffMemberMutation.isLoading}
            >
              Accept
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isResettingStaff}
        onClose={() => {
          setResettingStaffId(undefined);
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reset Staff Password</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure? This will immediately email the user a link to change
            their password.
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="brand.lightGray"
              color="gray.700"
              mr={3}
              onClick={() => {
                setResettingStaffId(undefined);
              }}
            >
              Close
            </Button>
            <Button
              colorScheme="brand.red"
              onClick={() => resetStaffPasswordMutation.mutate()}
              isDisabled={resetStaffPasswordMutation.isLoading}
            >
              {resetStaffPasswordMutation.isLoading ? "Resetting..." : "Accept"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
